import { useCallback, useState } from "react";
import {
  createFormulaCalculationId,
  createFormulaNodeId,
  toCalculationRequests,
} from "../../../utils/formulaCalculationMarkers";

/**
 * Advance past the current Process AI Manual follow-up.
 * @param {Array<object>} list
 * @returns {{ remaining: Array<object>, current: object|null }}
 */
export function shiftPendingFollowUp(list) {
  if (!Array.isArray(list) || list.length === 0) {
    return { remaining: [], current: null };
  }
  const remaining = list.slice(1);
  return {
    remaining,
    current: remaining[0] || null,
  };
}

function dialogStateFromFollowUp(item) {
  if (!item?.formula) return null;
  return {
    formula: item.formula,
    calculationId: item.calculationId,
    missingParameters: item.missingParameters,
    message: item.message,
    code: item.code,
    _kind: item._kind,
    insertPos: item.insertPos ?? null,
  };
}

/**
 * Client-side queue of formula calculation requests for the next ProcessWithAI call,
 * plus a separate Process AI needs_input/blocked follow-up list (sequential Manual dialogs).
 */
export function useFormulaCalculations() {
  const [queue, setQueue] = useState([]);
  /** @type {[Array<object>, Function]} */
  const [pendingFollowUps, setPendingFollowUps] = useState([]);
  /** @type {[object | null, Function]} */
  const [manualDialog, setManualDialog] = useState(null);

  const enqueueFormula = useCallback((formula) => {
    if (!formula?.formulaId) return null;
    const entry = {
      calculationId: createFormulaCalculationId(),
      formulaId: String(formula.formulaId),
      formulaVersion: String(formula.version || formula.formulaVersion || "1.0.0"),
      nodeId: createFormulaNodeId(),
      formula,
      queuedAt: Date.now(),
    };
    setQueue((prev) => [...prev, entry]);
    return entry;
  }, []);

  const removeFromQueue = useCallback((calculationId) => {
    setQueue((prev) =>
      prev.filter((q) => String(q.calculationId) !== String(calculationId)),
    );
  }, []);

  const clearQueue = useCallback(() => setQueue([]), []);

  const getCalculationRequests = useCallback(
    () => toCalculationRequests(queue),
    [queue],
  );

  const openManualDialog = useCallback((formula, extras = {}) => {
    if (!formula) return;
    setManualDialog({ formula, ...extras });
  }, []);

  /**
   * Start sequential Manual dialogs for Process AI needs_input / blocked rows.
   * Independent of calculationRequests queue (clearQueue must not wipe these).
   * @param {Array<{ formula: object, calculationId?: string, missingParameters?: string[], message?: string, code?: string, _kind?: string, insertPos?: number|null }>} items
   */
  const beginFollowUps = useCallback((items) => {
    const list = Array.isArray(items) ? items.filter((x) => x?.formula) : [];
    setPendingFollowUps(list);
    if (list.length === 0) {
      setManualDialog(null);
      return;
    }
    setManualDialog(dialogStateFromFollowUp(list[0]));
  }, []);

  /**
   * Close current Manual dialog. If Process AI follow-ups remain, open the next
   * without removing other formulas' pending chips.
   */
  const closeManualDialog = useCallback(() => {
    setPendingFollowUps((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) {
        setManualDialog(null);
        return [];
      }
      const { remaining, current } = shiftPendingFollowUp(prev);
      setManualDialog(dialogStateFromFollowUp(current));
      return remaining;
    });
  }, []);

  return {
    queue,
    enqueueFormula,
    removeFromQueue,
    clearQueue,
    getCalculationRequests,
    manualDialog,
    openManualDialog,
    closeManualDialog,
    pendingFollowUps,
    beginFollowUps,
  };
}
